import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenCheckIcon, BoxIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, LockKeyholeIcon, PlusIcon, RotateCcwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { setUserCustomFlashcardSets, subscribeToUserCustomFlashcardSets, type CustomFlashcardSet } from '../../../utils/firebase';
import { getCheckoutUrl, getStudyProgram } from '../../../utils/studyAccess';
import { useStudyMembership } from '../../../utils/useStudyMembership';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
const fieldClass = 'mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15';
type StudyView = 'dashboard' | 'quiz-sections' | 'quiz' | 'flashcard-library' | 'create-flashcard-set' | 'flashcards';
type QuizSection = {
  id: string;
  title: string;
  description: string;
  pastScore: string;
  questionCount: number;
};
type QuestionFormat = 'multiple-choice' | 'open-ended' | 'matching' | 'true-false';
type QuizSettings = {
  questionCount: number;
  timeLimitMinutes: number | null;
  revealIncorrectAnswers: boolean;
  formats: QuestionFormat[];
};
type Flashcard = {
  topic: string;
  front: string;
  back: string;
};
type FlashcardSet = {
  id: string;
  title: string;
  description: string;
  cardCount: number;
  cards: Flashcard[];
  isCustom?: boolean;
};
const questions = [{
  prompt: 'Which action is most appropriate before initiating parenteral nutrition in a patient at high risk for refeeding syndrome?',
  answers: ['Advance to goal calories on day one', 'Assess and correct key electrolyte abnormalities', 'Avoid protein during the first 48 hours', 'Use peripheral access for all formulations'],
  correct: 1,
  rationale: 'High-risk patients require careful baseline assessment and correction of electrolyte abnormalities, with calories advanced cautiously.'
}, {
  prompt: 'For a stable adult receiving enteral nutrition, which measure is most useful for evaluating tolerance over time?',
  answers: ['A single gastric residual volume', 'Trend in clinical status, intake, stool pattern, and abdominal findings', 'The patient’s height alone', 'Daily interruption of the feeding'],
  correct: 1,
  rationale: 'Tolerance is a clinical pattern, not a single number. Consider the full picture and institutional protocols.'
}];
const flashcardSets: FlashcardSet[] = [{
  id: 'nutrition-assessment',
  title: 'Nutrition Assessment',
  description: 'Screening, clinical findings, and practical assessment priorities.',
  cardCount: 24,
  cards: [{
    topic: 'Nutrition assessment',
    front: 'What should be evaluated when assessing nutrition support tolerance?',
    back: 'Clinical status, delivery volume, GI findings, hydration, labs, and individual response.'
  }, {
    topic: 'Nutrition assessment',
    front: 'What is the purpose of nutrition screening?',
    back: 'To quickly identify people at risk who need a more comprehensive nutrition assessment.'
  }, {
    topic: 'Nutrition assessment',
    front: 'Why is a weight trend more useful than a single weight?',
    back: 'A trend provides context about change over time and helps identify clinically meaningful loss or gain.'
  }]
}, {
  id: 'medical-nutrition-therapy',
  title: 'Medical Nutrition Therapy',
  description: 'High-yield interventions for common clinical nutrition scenarios.',
  cardCount: 32,
  cards: [{
    topic: 'Medical nutrition therapy',
    front: 'What is a core goal of individualized medical nutrition therapy?',
    back: 'To translate clinical needs into realistic nutrition interventions that support health outcomes.'
  }, {
    topic: 'Medical nutrition therapy',
    front: 'When should education be adapted for readiness to learn?',
    back: 'Whenever barriers, literacy, culture, motivation, or the care setting affect a person’s ability to act on guidance.'
  }, {
    topic: 'Medical nutrition therapy',
    front: 'Why should follow-up be part of an intervention plan?',
    back: 'It lets the dietitian assess response, resolve barriers, and adjust the plan over time.'
  }]
}, {
  id: 'nutrition-support',
  title: 'Nutrition Support',
  description: 'Enteral and parenteral nutrition principles for clinical practice.',
  cardCount: 28,
  cards: [{
    topic: 'Parenteral nutrition',
    front: 'What is one key goal when advancing nutrition in someone at refeeding risk?',
    back: 'Advance cautiously while closely monitoring and repleting electrolytes as clinically indicated.'
  }, {
    topic: 'Enteral nutrition',
    front: 'What is a core advantage of enteral nutrition when the GI tract is functional?',
    back: 'It supports use of the gut and is generally preferred when clinically appropriate.'
  }, {
    topic: 'Nutrition support',
    front: 'What is a key consideration when monitoring nutrition support?',
    back: 'Monitor delivery, tolerance, fluid balance, labs, and the person’s changing clinical condition.'
  }]
}];
const topics = [['Nutrition assessment', 78], ['Enteral nutrition', 64], ['Parenteral nutrition', 49], ['Clinical management', 56]];
const quizSections: QuizSection[] = [{
  id: 'nutrition-assessment',
  title: 'Nutrition Assessment',
  description: 'Screening, anthropometrics, and clinical assessment findings.',
  pastScore: '78% · 8 questions',
  questionCount: 12
}, {
  id: 'medical-nutrition-therapy',
  title: 'Medical Nutrition Therapy',
  description: 'Nutrition diagnoses, interventions, and follow-up planning.',
  pastScore: '84% · 10 questions',
  questionCount: 14
}, {
  id: 'enteral-nutrition',
  title: 'Enteral Nutrition',
  description: 'Formula selection, access routes, delivery, and monitoring.',
  pastScore: '64% · 11 questions',
  questionCount: 16
}, {
  id: 'parenteral-nutrition',
  title: 'Parenteral Nutrition',
  description: 'Indications, formulation, complications, and refeeding risk.',
  pastScore: '49% · 9 questions',
  questionCount: 15
}, {
  id: 'clinical-management',
  title: 'Clinical Management',
  description: 'Clinical decision-making across complex nutrition scenarios.',
  pastScore: '72% · 7 questions',
  questionCount: 13
}];
export function Study() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const selectedProduct = getStudyProgram(searchParams.get('product'));
  const membership = useStudyMembership(selectedProduct?.id);
  const selectedProgram = selectedProduct?.name ?? 'CNSC Exam Prep';
  const [view, setView] = useState<StudyView>('dashboard');
  const [selectedFlashcardSet, setSelectedFlashcardSet] = useState<FlashcardSet | null>(null);
  const [selectedQuizSectionIds, setSelectedQuizSectionIds] = useState<string[]>([]);
  const [quizSettings, setQuizSettings] = useState<QuizSettings>({
    questionCount: 10,
    timeLimitMinutes: null,
    revealIncorrectAnswers: true,
    formats: ['multiple-choice']
  });
  const [customSets, setCustomSets] = useState<CustomFlashcardSet[]>([]);
  const [customSetsLoading, setCustomSetsLoading] = useState(Boolean(user));
  const [customSetsError, setCustomSetsError] = useState<string | null>(null);
  useEffect(() => {
    if (!user) {
      setCustomSets([]);
      setCustomSetsLoading(false);
      setCustomSetsError(null);
      return;
    }
    setCustomSetsLoading(true);
    setCustomSetsError(null);
    return subscribeToUserCustomFlashcardSets(user.uid, sets => {
      setCustomSets(sets);
      setCustomSetsLoading(false);
    }, () => {
      setCustomSetsLoading(false);
      setCustomSetsError('Your saved sets could not be loaded. Please refresh and try again.');
    });
  }, [user?.uid]);
  const openFlashcardLibrary = () => {
    setSelectedFlashcardSet(null);
    setView('flashcard-library');
  };
  const selectSet = (set: FlashcardSet) => {
    setSelectedFlashcardSet(set);
    setView('flashcards');
  };
  const saveCustomSet = async (set: CustomFlashcardSet) => {
    if (!user) throw new Error('Please sign in to save your flashcard set.');
    await setUserCustomFlashcardSets(user.uid, [set, ...customSets]);
    selectSet(toStudySet(set));
  };
  const openQuizSections = () => {
    setSelectedQuizSectionIds([]);
    setView('quiz-sections');
  };
  const toggleQuizSection = (sectionId: string) => {
    setSelectedQuizSectionIds(current => current.includes(sectionId) ? current.filter(id => id !== sectionId) : [...current, sectionId]);
  };
  const selectedQuizSections = quizSections.filter(section => selectedQuizSectionIds.includes(section.id));
  if (!isPreview && !selectedProduct) {
    return <InvalidStudyProgramState data-id="element-2792" />;
  }
  if (!isPreview && (authLoading || membership.status === 'loading')) {
    return <LoadingState data-id="element-2793" />;
  }
  if (!isPreview && !user) {
    navigate('/login', {
      state: {
        from: `${location.pathname}${location.search}`
      },
      replace: true
    });
    return <LoadingState data-id="element-2794" />;
  }
  if (!isPreview && !membership.hasAccess && selectedProduct) {
    return <LockedStudyState programId={selectedProduct.id} programName={selectedProduct.name} data-id="element-2795" />;
  }
  return <div className="min-h-screen bg-white px-4 py-7 text-[#1E293B] md:px-8 md:py-10" style={{
    fontFamily: FONT
  }} data-id="element-2796">
      <div className="mx-auto max-w-7xl" data-id="element-2797">
        <header className="mb-7 flex flex-col justify-between gap-5 border-b border-[#EDF1F7] pb-6 md:flex-row md:items-end" data-id="element-2798">
          <div data-id="element-2799">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-2800">
              RD Ready member space
            </p>
            <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-2801">
              {selectedProgram}
            </h1>
            <p className="mt-2 text-sm text-[#5A6B7B]" data-id="element-2802">Study with intention.</p>
          </div>
          <nav className="flex flex-wrap gap-2" aria-label="Study navigation" data-id="element-2803">
            {([['dashboard', 'Overview'], ['quiz-sections', 'Quiz mode'], ['flashcard-library', 'Flashcards']] as [StudyView, string][]).map(([target, label]) => {
            const active = view === target || target === 'quiz-sections' && view === 'quiz' || target === 'flashcard-library' && (view === 'flashcards' || view === 'create-flashcard-set');
            return <button key={target} type="button" onClick={() => target === 'flashcard-library' ? openFlashcardLibrary() : target === 'quiz-sections' ? openQuizSections() : setView(target)} className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${active ? 'text-white' : 'border border-[#E2E8F0] bg-white text-[#475569] hover:border-[#2563EB] hover:text-[#2563EB]'}`} style={active ? {
              backgroundColor: BLUE
            } : undefined} data-id="element-2804">
                  {label}
                </button>;
          })}
          </nav>
        </header>

        {view === 'dashboard' && <Dashboard onStartQuiz={openQuizSections} onStartCards={openFlashcardLibrary} data-id="element-2805" />}
        {view === 'quiz-sections' && <QuizSectionChooser sections={quizSections} selectedIds={selectedQuizSectionIds} onBack={() => setView('dashboard')} onToggle={toggleQuizSection} onStart={settings => {
        setQuizSettings(settings);
        setView('quiz');
      }} data-id="element-2806" />}
        {view === 'quiz' && <Quiz selectedSections={selectedQuizSections} settings={quizSettings} onBack={() => setView('quiz-sections')} onFinish={() => setView('dashboard')} data-id="element-2807" />}
        {view === 'flashcard-library' && <FlashcardLibrary builtInSets={flashcardSets} customSets={customSets.map(toStudySet)} customSetsLoading={customSetsLoading} customSetsError={customSetsError} onCreate={() => setView('create-flashcard-set')} onSelect={selectSet} data-id="element-2808" />}
        {view === 'create-flashcard-set' && <CreateFlashcardSet onCancel={openFlashcardLibrary} onSave={saveCustomSet} data-id="element-2809" />}
        {view === 'flashcards' && selectedFlashcardSet && <Flashcards set={selectedFlashcardSet} onBack={openFlashcardLibrary} onFinish={() => setView('dashboard')} data-id="element-2810" />}
      </div>
    </div>;
}
function toStudySet(set: CustomFlashcardSet): FlashcardSet {
  return {
    id: set.id,
    title: set.title,
    description: set.description,
    cardCount: set.cards.length,
    cards: set.cards.map(card => ({
      topic: card.topic || 'Your study notes',
      front: card.front,
      back: card.back
    })),
    isCustom: true
  };
}
function LoadingState() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-white" data-id="element-2811">
      <span className="h-7 w-7 animate-spin rounded-full border-2 border-t-transparent" style={{
      borderColor: BLUE,
      borderTopColor: 'transparent'
    }} data-id="element-2812" />
    </div>;
}
function LockedStudyState({
  programId,
  programName
}: {
  programId: 'rd-ready-rd-exam' | 'rd-ready-cnsc-prep';
  programName: string;
}) {
  return <div className="min-h-screen bg-white px-5 py-20 md:px-8" style={{
    fontFamily: FONT
  }} data-id="element-2813">
      <div className="mx-auto max-w-2xl rounded-3xl border border-[#E2E8F0] bg-white p-8 text-center shadow-[0_4px_28px_rgba(15,23,42,.06)] md:p-12" data-id="element-2814">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{
        backgroundColor: '#EEF4FF',
        color: BLUE
      }} data-id="element-2815">
          <LockKeyholeIcon className="h-6 w-6" data-id="element-2816" />
        </span>
        <p className="mt-7 text-[10px] font-bold uppercase tracking-[0.2em]" style={{
        color: BLUE
      }} data-id="element-2817">
          Purchase required
        </p>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A] md:text-4xl" data-id="element-2818">
          Unlock {programName} to start studying.
        </h1>
        <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2819">
          This program is a one-time $150 purchase. Access is granted only to
          the account with a paid order for this exact study program.
        </p>
        <Link to={getCheckoutUrl(programId)} className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5" style={{
        backgroundColor: BLUE
      }} data-id="element-2820">
          Unlock for $150 <ArrowRightIcon className="h-4 w-4" data-id="element-2821" />
        </Link>
        <p className="mt-7 text-xs leading-relaxed text-[#94A3B8]" data-id="element-2822">
          Prototype note: Firestore rules and server-side entitlement checks
          must secure study content in production.
        </p>
      </div>
    </div>;
}
function InvalidStudyProgramState() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-white px-5 text-center" data-id="element-2823">
      <div className="max-w-md rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-[0_4px_28px_rgba(15,23,42,.06)]" data-id="element-2824">
        <LockKeyholeIcon className="mx-auto h-8 w-8 text-[#2563EB]" data-id="element-2825" />
        <h1 className="mt-4 text-2xl font-bold text-[#0F172A]" data-id="element-2826">
          Select a study program
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2827">
          Choose RD Exam Prep or CNSC Exam Prep to review access and begin
          studying.
        </p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white" data-id="element-2828">
          Explore programs
        </Link>
      </div>
    </div>;
}
function Dashboard({
  onStartQuiz,
  onStartCards
}: {
  onStartQuiz: () => void;
  onStartCards: () => void;
}) {
  return <div className="grid gap-6 lg:grid-cols-[1.45fr_.8fr]" data-id="element-2829">
      <section data-id="element-2830">
        <div className="rounded-3xl p-7 text-white md:p-9" style={{
        backgroundColor: BLUE
      }} data-id="element-2831">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70" data-id="element-2832">
            Your next step
          </p>
          <h2 className="mt-3 text-2xl font-bold md:text-3xl" data-id="element-2833">
            Build confidence in parenteral nutrition.
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85" data-id="element-2834">
            Your accuracy suggests a short practice set followed by a flashcard
            review.
          </p>
          <div className="mt-7 flex flex-wrap gap-3" data-id="element-2835">
            <button type="button" onClick={onStartQuiz} className="rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-transform hover:-translate-y-0.5" style={{
            color: BLUE
          }} data-id="element-2836">
              Start a quiz
            </button>
            <button type="button" onClick={onStartCards} className="rounded-full border border-white/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white/10" data-id="element-2837">
              Review cards
            </button>
          </div>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-2838">
          <StudyCard icon={BookOpenCheckIcon} label="Questions completed" value="42" note="This week" data-id="element-2839" />
          <StudyCard icon={BoxIcon} label="Flashcards reviewed" value="128" note="This week" data-id="element-2840" />
        </div>
        <section className="mt-6 rounded-2xl border border-[#E2E8F0] bg-[#F7F9FC] p-6" data-id="element-2841">
          <div className="flex items-center justify-between" data-id="element-2842">
            <h2 className="text-xl font-bold text-[#0F172A]" data-id="element-2843">Topic progress</h2>
            <span className="text-xs font-semibold" style={{
            color: BLUE
          }} data-id="element-2844">
              Updated today
            </span>
          </div>
          <div className="mt-6 space-y-5" data-id="element-2845">
            {topics.map(([topic, progress]) => <TopicProgress key={topic} topic={topic} progress={progress as number} data-id="element-2846" />)}
          </div>
        </section>
      </section>
      <aside className="rounded-2xl border border-[#E2E8F0] bg-[#F0FBF9] p-6" data-id="element-2847">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{
        color: TEAL
      }} data-id="element-2848">
          Study rhythm
        </p>
        <div className="mt-5 border-b border-[#D5EFEA] pb-6" data-id="element-2849">
          <p className="text-5xl font-extrabold text-[#0F172A]" data-id="element-2850">4</p>
          <p className="mt-1 text-sm text-[#5A6B7B]" data-id="element-2851">active days this week</p>
        </div>
        <div className="space-y-4 py-6 text-sm text-[#334155]" data-id="element-2852">
          <p className="flex gap-3" data-id="element-2853">
            <CheckIcon className="h-4 w-4 shrink-0" style={{
            color: TEAL
          }} strokeWidth={3} data-id="element-2854" />
            Keep sessions short and targeted.
          </p>
          <p className="flex gap-3" data-id="element-2855">
            <CheckIcon className="h-4 w-4 shrink-0" style={{
            color: TEAL
          }} strokeWidth={3} data-id="element-2856" />
            Use misses to choose the next topic.
          </p>
        </div>
        <p className="border-t border-[#D5EFEA] pt-5 text-xs leading-relaxed text-[#94A3B8]" data-id="element-2857">
          Content is representative study material for learning support and does
          not reproduce official exam content.
        </p>
      </aside>
    </div>;
}
function StudyCard({
  icon: Icon,
  label,
  value,
  note
}: {
  icon: typeof BookOpenCheckIcon;
  label: string;
  value: string;
  note: string;
}) {
  return <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-[0_2px_16px_rgba(15,23,42,.05)]" data-id="element-2858">
      <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{
      backgroundColor: '#EEF4FF'
    }} data-id="element-2859">
        <Icon className="h-5 w-5" style={{
        color: BLUE
      }} data-id="element-2860" />
      </span>
      <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.15em] text-[#94A3B8]" data-id="element-2861">
        {label}
      </p>
      <p className="mt-1 text-3xl font-extrabold text-[#0F172A]" data-id="element-2862">{value}</p>
      <p className="mt-1 text-xs text-[#94A3B8]" data-id="element-2863">{note}</p>
    </div>;
}
function TopicProgress({
  topic,
  progress
}: {
  topic: string;
  progress: number;
}) {
  return <div data-id="element-2864">
      <div className="mb-2 flex justify-between text-sm" data-id="element-2865">
        <span className="font-medium text-[#334155]" data-id="element-2866">{topic}</span>
        <span className="text-[#94A3B8]" data-id="element-2867">{progress}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#E2E8F0]" data-id="element-2868">
        <div className="h-full rounded-full" style={{
        width: `${progress}%`,
        backgroundColor: BLUE
      }} data-id="element-2869" />
      </div>
    </div>;
}
function QuizSectionChooser({
  sections,
  selectedIds,
  onBack,
  onToggle,
  onStart
}: {
  sections: QuizSection[];
  selectedIds: string[];
  onBack: () => void;
  onToggle: (sectionId: string) => void;
  onStart: (settings: QuizSettings) => void;
}) {
  const [setupOpen, setSetupOpen] = useState(false);
  const selectedSections = sections.filter(section => selectedIds.includes(section.id));
  const totalQuestions = selectedSections.reduce((total, section) => total + section.questionCount, 0);
  return <section className="mx-auto max-w-5xl" data-id="element-2870">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B] transition-colors hover:text-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-2871">
        <ChevronLeftIcon className="h-4 w-4" data-id="element-2872" /> Study overview
      </button>
      <div className="mt-5 rounded-3xl border border-[#E2E8F0] bg-[#F7F9FC] p-6 md:p-9" data-id="element-2873">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-2874">
          Practice quiz
        </p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-2875">
          Choose your material sections
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2876">
          Select one section for a focused quiz, or combine multiple sections
          for broader review. Scores below are representative past results.
        </p>

        <fieldset className="mt-8" data-id="element-2877">
          <legend className="sr-only" data-id="element-2878">Choose quiz material sections</legend>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2879">
            {sections.map(section => {
            const isSelected = selectedIds.includes(section.id);
            return <label key={section.id} className={`group relative flex min-h-[216px] cursor-pointer flex-col rounded-2xl border bg-white p-5 transition-colors ${isSelected ? 'border-[#2563EB] bg-[#F5F8FF]' : 'border-[#E2E8F0] hover:border-[#93C5FD]'} focus-within:ring-2 focus-within:ring-[#2563EB] focus-within:ring-offset-2`} data-id="element-2880">
                  <input type="checkbox" checked={isSelected} onChange={() => onToggle(section.id)} className="peer sr-only" data-id="element-2881" />
                  <span aria-hidden="true" className={`flex h-6 w-6 items-center justify-center rounded-md border ${isSelected ? 'border-[#2563EB] bg-[#2563EB] text-white' : 'border-[#CBD5E1] bg-white text-transparent'}`} data-id="element-2882">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-2883" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold text-[#0F172A]" data-id="element-2884">
                    {section.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2885">
                    {section.description}
                  </p>
                  <span className="mt-auto border-t border-[#E2E8F0] pt-4 text-xs font-semibold text-[#0F766E]" data-id="element-2886">
                    Past score: {section.pastScore}
                  </span>
                </label>;
          })}
          </div>
        </fieldset>

        <div aria-live="polite" className="mt-7 flex flex-col gap-4 rounded-2xl border border-[#C7EFE8] bg-[#F0FBF9] p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-2887">
          <div data-id="element-2888">
            <p className="text-sm font-bold text-[#0F172A]" data-id="element-2889">
              {selectedIds.length}{' '}
              {selectedIds.length === 1 ? 'section' : 'sections'} selected
            </p>
            <p className="mt-1 text-xs text-[#52627E]" data-id="element-2890">
              {selectedIds.length ? `${totalQuestions} representative questions in your selected scope.` : 'Select at least one section to start your quiz.'}
            </p>
          </div>
          <button type="button" disabled={!selectedIds.length} onClick={() => setSetupOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0" data-id="element-2891">
            Start quiz <ArrowRightIcon className="h-4 w-4" data-id="element-2892" />
          </button>
        </div>
      </div>
      {setupOpen && <QuizSetupDialog selectedSectionCount={selectedIds.length} onClose={() => setSetupOpen(false)} onStart={settings => {
      setSetupOpen(false);
      onStart(settings);
    }} data-id="element-2893" />}
    </section>;
}
function QuizSetupDialog({
  selectedSectionCount,
  onClose,
  onStart
}: {
  selectedSectionCount: number;
  onClose: () => void;
  onStart: (settings: QuizSettings) => void;
}) {
  const [questionCount, setQuestionCount] = useState(10);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | null>(null);
  const [revealIncorrectAnswers, setRevealIncorrectAnswers] = useState(true);
  const [formats, setFormats] = useState<QuestionFormat[]>(['multiple-choice']);
  const toggleFormat = (format: QuestionFormat) => {
    setFormats(current => current.includes(format) ? current.filter(value => value !== format) : [...current, format]);
  };
  const formatOptions: {
    id: QuestionFormat;
    label: string;
    note: string;
  }[] = [{
    id: 'multiple-choice',
    label: 'Multiple choice',
    note: 'Choose one answer'
  }, {
    id: 'open-ended',
    label: 'Open ended',
    note: 'Write your response'
  }, {
    id: 'matching',
    label: 'Matching',
    note: 'Pair related concepts'
  }, {
    id: 'true-false',
    label: 'True or false',
    note: 'Quick recall check'
  }];
  return <div className="fixed inset-0 z-[70] flex items-end bg-[#0F172A]/45 p-4 sm:items-center sm:justify-center" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-2894">
      <section role="dialog" aria-modal="true" aria-labelledby="quiz-setup-title" className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8" data-id="element-2895">
        <div className="flex items-start justify-between gap-5" data-id="element-2896">
          <div data-id="element-2897">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-2898">
              Quiz setup
            </p>
            <h2 id="quiz-setup-title" className="mt-2 text-2xl font-extrabold text-[#0F172A]" data-id="element-2899">
              Tailor this practice session
            </h2>
            <p className="mt-2 text-sm text-[#5A6B7B]" data-id="element-2900">
              {selectedSectionCount} selected{' '}
              {selectedSectionCount === 1 ? 'section' : 'sections'} will shape
              your quiz scope.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#0F172A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" aria-label="Close quiz setup" data-id="element-2901">
            <XIcon className="h-5 w-5" data-id="element-2902" />
          </button>
        </div>

        <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-2903">
          <label className="text-sm font-semibold text-[#334155]" data-id="element-2904">
            Number of questions
            <select value={questionCount} onChange={event => setQuestionCount(Number(event.target.value))} className={fieldClass} data-id="element-2905">
              {[5, 10, 15, 20].map(count => <option key={count} value={count} data-id="element-2906">
                  {count} questions
                </option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-[#334155]" data-id="element-2907">
            Time limit
            <select value={timeLimitMinutes ?? 'none'} onChange={event => setTimeLimitMinutes(event.target.value === 'none' ? null : Number(event.target.value))} className={fieldClass} data-id="element-2908">
              <option value="none" data-id="element-2909">No time limit</option>
              <option value="10" data-id="element-2910">10 minutes</option>
              <option value="20" data-id="element-2911">20 minutes</option>
              <option value="30" data-id="element-2912">30 minutes</option>
            </select>
          </label>
        </div>

        <label className="mt-6 flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-[#C7EFE8] bg-[#F0FBF9] p-5 focus-within:ring-2 focus-within:ring-[#2563EB] focus-within:ring-offset-2" data-id="element-2913">
          <span data-id="element-2914">
            <span className="block text-sm font-bold text-[#0F172A]" data-id="element-2915">
              Reveal incorrect answers immediately
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-[#52627E]" data-id="element-2916">
              Show the correct answer and rationale after an incorrect choice.
            </span>
          </span>
          <input type="checkbox" checked={revealIncorrectAnswers} onChange={event => setRevealIncorrectAnswers(event.target.checked)} className="h-5 w-5 rounded border-[#94A3B8] text-[#2563EB] focus:ring-[#2563EB]" data-id="element-2917" />
        </label>

        <fieldset className="mt-6" data-id="element-2918">
          <legend className="text-sm font-bold text-[#334155]" data-id="element-2919">
            Question formats
          </legend>
          <p className="mt-1 text-xs text-[#64748B]" data-id="element-2920">
            Choose at least one format for this session.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-2921">
            {formatOptions.map(format => {
            const checked = formats.includes(format.id);
            return <label key={format.id} className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${checked ? 'border-[#2563EB] bg-[#F5F8FF]' : 'border-[#E2E8F0] bg-white'}`} data-id="element-2922">
                  <input type="checkbox" checked={checked} onChange={() => toggleFormat(format.id)} className="mt-0.5 h-4 w-4 rounded border-[#94A3B8] text-[#2563EB] focus:ring-[#2563EB]" data-id="element-2923" />
                  <span data-id="element-2924">
                    <span className="block text-sm font-semibold text-[#0F172A]" data-id="element-2925">
                      {format.label}
                    </span>
                    <span className="mt-1 block text-xs text-[#64748B]" data-id="element-2926">
                      {format.note}
                    </span>
                  </span>
                </label>;
          })}
          </div>
        </fieldset>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-2927">
          <button type="button" onClick={onClose} className="rounded-full border border-[#CBD5E1] px-6 py-3 text-sm font-semibold text-[#475569] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]" data-id="element-2928">
            Cancel
          </button>
          <button type="button" disabled={!formats.length} onClick={() => onStart({
          questionCount,
          timeLimitMinutes,
          revealIncorrectAnswers,
          formats
        })} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-2929">
            Start quiz <ArrowRightIcon className="h-4 w-4" data-id="element-2930" />
          </button>
        </div>
      </section>
    </div>;
}
type GeneratedQuizQuestion = {
  format: QuestionFormat;
  prompt: string;
  options?: string[];
  answer: string;
  rationale: string;
  matchTerm?: string;
};
function Quiz({
  selectedSections,
  settings,
  onBack,
  onFinish
}: {
  selectedSections: QuizSection[];
  settings: QuizSettings;
  onBack: () => void;
  onFinish: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [response, setResponse] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(settings.timeLimitMinutes ? settings.timeLimitMinutes * 60 : null);
  const quizQuestions = Array.from({
    length: settings.questionCount
  }, (_, questionIndex) => makeQuizQuestion(settings.formats[questionIndex % settings.formats.length], questionIndex));
  useEffect(() => {
    if (secondsRemaining === null) return;
    if (secondsRemaining <= 0) {
      onFinish();
      return;
    }
    const timer = window.setInterval(() => setSecondsRemaining(current => current === null ? null : current - 1), 1000);
    return () => window.clearInterval(timer);
  }, [onFinish, secondsRemaining]);
  const question = quizQuestions[index];
  const isLast = index === quizQuestions.length - 1;
  const normalizedResponse = response.trim().toLowerCase();
  const normalizedAnswer = question.answer.toLowerCase();
  const isCorrect = question.format === 'open-ended' ? normalizedResponse.includes(normalizedAnswer) : normalizedResponse === normalizedAnswer;
  const scopeLabel = selectedSections.map(section => section.title).join(' · ');
  const formatLabel = formatQuizLabel(question.format);
  const checkAnswer = () => {
    if (!response.trim()) return;
    if (!isCorrect && settings.revealIncorrectAnswers) setRevealed(true);else setRevealed(true);
  };
  const next = () => {
    if (isLast) {
      onFinish();
      return;
    }
    setIndex(value => value + 1);
    setResponse('');
    setRevealed(false);
  };
  const chooseResponse = (value: string) => {
    if (revealed) return;
    setResponse(value);
    if (settings.revealIncorrectAnswers && !isCorrectResponse(value, question)) {
      setRevealed(true);
    }
  };
  return <section className="mx-auto max-w-3xl rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-[0_4px_28px_rgba(15,23,42,.06)] md:p-9" data-id="element-2931">
      <div className="flex items-start justify-between gap-4 text-xs font-bold uppercase tracking-[0.14em] text-[#94A3B8]" data-id="element-2932">
        <div data-id="element-2933">
          <span className="block" data-id="element-2934">Practice quiz · {formatLabel}</span>
          <span className="mt-2 block max-w-md normal-case tracking-normal text-[#0F766E]" data-id="element-2935">
            {scopeLabel}
          </span>
        </div>
        <div className="shrink-0 text-right" data-id="element-2936">
          {secondsRemaining !== null && <span className="mb-1 block text-[#0F766E]" data-id="element-2937">
              {formatQuizTime(secondsRemaining)}
            </span>}
          <span data-id="element-2938">
            {index + 1} / {quizQuestions.length}
          </span>
        </div>
      </div>
      <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-[#E2E8F0]" data-id="element-2939">
        <div className="h-full rounded-full transition-all" style={{
        width: `${(index + 1) / quizQuestions.length * 100}%`,
        backgroundColor: BLUE
      }} data-id="element-2940" />
      </div>
      <h1 className="mt-9 text-2xl font-bold leading-snug text-[#0F172A] md:text-3xl" data-id="element-2941">
        {question.prompt}
      </h1>

      <div className="mt-8" data-id="element-2942">
        {(question.format === 'multiple-choice' || question.format === 'true-false') && <div className="space-y-3" role="radiogroup" aria-label={formatLabel} data-id="element-2943">
            {question.options?.map(option => <button type="button" key={option} role="radio" aria-checked={response === option} disabled={revealed} onClick={() => chooseResponse(option)} className={`w-full rounded-xl border p-4 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 ${revealed && option === question.answer ? 'border-[#0F9E8E] bg-[#F0FBF9]' : response === option ? 'border-[#2563EB] bg-[#F5F8FF]' : 'border-[#E2E8F0] hover:border-[#2563EB]'}`} data-id="element-2944">
                {option}
              </button>)}
          </div>}
        {question.format === 'open-ended' && <label className="block text-sm font-semibold text-[#334155]" data-id="element-2945">
            Your response
            <textarea rows={4} value={response} disabled={revealed} onChange={event => setResponse(event.target.value)} placeholder="Write your answer in your own words" className={`${fieldClass} resize-y disabled:bg-[#F8FAFC]`} data-id="element-2946" />
          </label>}
        {question.format === 'matching' && <label className="block text-sm font-semibold text-[#334155]" data-id="element-2947">
            Match “{question.matchTerm}” with the correct description
            <select value={response} disabled={revealed} onChange={event => chooseResponse(event.target.value)} className={`${fieldClass} disabled:bg-[#F8FAFC]`} data-id="element-2948">
              <option value="" data-id="element-2949">Choose a match</option>
              {question.options?.map(option => <option key={option} value={option} data-id="element-2950">
                  {option}
                </option>)}
            </select>
          </label>}
      </div>

      {revealed && <div className={`mt-6 rounded-r-xl border-l-4 p-4 ${isCorrect ? 'border-[#0F9E8E] bg-[#F0FBF9]' : 'border-[#2563EB] bg-[#F5F8FF]'}`} data-id="element-2951">
          <p className="text-xs font-bold uppercase tracking-[0.15em]" style={{
        color: isCorrect ? TEAL : BLUE
      }} data-id="element-2952">
            {isCorrect ? 'Correct' : `Correct answer: ${question.answer}`}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-[#334155]" data-id="element-2953">
            {question.rationale}
          </p>
        </div>}

      <div className="mt-8 flex justify-between gap-4" data-id="element-2954">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.12em] text-[#94A3B8] transition-colors hover:text-[#0F172A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-2955">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-2956" /> Change sections
        </button>
        <button type="button" disabled={!response.trim()} onClick={() => revealed ? next() : checkAnswer()} className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:opacity-40 disabled:hover:translate-y-0" style={{
        backgroundColor: BLUE
      }} data-id="element-2957">
          {revealed ? isLast ? 'Finish quiz' : 'Next question' : 'Check answer'}{' '}
          <ChevronRightIcon className="h-4 w-4" data-id="element-2958" />
        </button>
      </div>
    </section>;
}
function makeQuizQuestion(format: QuestionFormat, index: number): GeneratedQuizQuestion {
  const sampleIndex = index % 2;
  if (format === 'multiple-choice') {
    const item = questions[sampleIndex];
    return {
      format,
      prompt: item.prompt,
      options: item.answers,
      answer: item.answers[item.correct],
      rationale: item.rationale
    };
  }
  if (format === 'true-false') {
    return sampleIndex === 0 ? {
      format,
      prompt: 'True or false: Enteral nutrition is generally preferred when the GI tract is functional and clinically appropriate.',
      options: ['True', 'False'],
      answer: 'True',
      rationale: 'When feasible, enteral nutrition supports use of the GI tract and is generally preferred.'
    } : {
      format,
      prompt: 'True or false: A single gastric residual volume should be used alone to determine enteral feeding tolerance.',
      options: ['True', 'False'],
      answer: 'False',
      rationale: 'Tolerance is assessed from the broader clinical pattern, including intake, GI findings, and clinical status.'
    };
  }
  if (format === 'matching') {
    return sampleIndex === 0 ? {
      format,
      prompt: 'Match the nutrition support concept to its definition.',
      matchTerm: 'Refeeding risk',
      options: ['Need for cautious calorie advancement with electrolyte monitoring', 'A formula delivery route through the GI tract', 'A rapid nutrition screening tool'],
      answer: 'Need for cautious calorie advancement with electrolyte monitoring',
      rationale: 'Refeeding risk requires cautious advancement and close electrolyte monitoring.'
    } : {
      format,
      prompt: 'Match the assessment concept to its definition.',
      matchTerm: 'Nutrition screening',
      options: ['A rapid process to identify nutrition risk', 'A complete nutrition diagnosis', 'A method for calculating fluid needs'],
      answer: 'A rapid process to identify nutrition risk',
      rationale: 'Screening rapidly identifies people who may need comprehensive assessment.'
    };
  }
  return sampleIndex === 0 ? {
    format,
    prompt: 'In a few words, what should be monitored when advancing nutrition in someone at refeeding risk?',
    answer: 'electrolytes',
    rationale: 'Electrolytes should be monitored closely and repleted as clinically indicated during cautious advancement.'
  } : {
    format,
    prompt: 'In a few words, what is the purpose of nutrition screening?',
    answer: 'risk',
    rationale: 'Nutrition screening identifies people at nutrition risk who may need further assessment.'
  };
}
function formatQuizLabel(format: QuestionFormat) {
  return format === 'multiple-choice' ? 'Multiple choice' : format === 'open-ended' ? 'Open ended' : format === 'matching' ? 'Matching' : 'True or false';
}
function isCorrectResponse(value: string, question: GeneratedQuizQuestion) {
  const response = value.trim().toLowerCase();
  const answer = question.answer.toLowerCase();
  return question.format === 'open-ended' ? response.includes(answer) : response === answer;
}
function formatQuizTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
function FlashcardLibrary({
  builtInSets,
  customSets,
  customSetsLoading,
  customSetsError,
  onCreate,
  onSelect
}: {
  builtInSets: FlashcardSet[];
  customSets: FlashcardSet[];
  customSetsLoading: boolean;
  customSetsError: string | null;
  onCreate: () => void;
  onSelect: (set: FlashcardSet) => void;
}) {
  return <section className="mx-auto max-w-5xl" data-id="element-2959">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-2960">
        Flashcard library
      </p>
      <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-2961">
        Choose a set to review
      </h2>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2962">
        Focus on one high-yield area at a time with active recall practice.
      </p>

      <div className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-2963">
        <button type="button" onClick={onCreate} className="rounded-2xl border-2 border-dashed border-[#93C5FD] bg-[#F5F8FF] p-6 text-left transition-colors hover:border-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-2964">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#2563EB] shadow-sm" data-id="element-2965">
            <PlusIcon className="h-5 w-5" data-id="element-2966" />
          </span>
          <h3 className="mt-6 text-xl font-bold text-[#0F172A]" data-id="element-2967">
            Create your own set
          </h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[#52627E]" data-id="element-2968">
            Turn your notes, missed questions, and study reminders into private
            flashcards that stay with your account.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]" data-id="element-2969">
            Build a set <ArrowRightIcon className="h-4 w-4" data-id="element-2970" />
          </span>
        </button>

        <aside className="rounded-2xl border border-[#C7EFE8] bg-[#F0FBF9] p-6" data-id="element-2971">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0F766E]" data-id="element-2972">
            Your account, your notes
          </p>
          <h3 className="mt-4 text-xl font-bold text-[#0F172A]" data-id="element-2973">
            Saved securely for you
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-2974">
            Your personal decks sync to your signed-in RD Ready account, so they
            are available after you sign out or switch devices.
          </p>
        </aside>
      </div>

      <div className="mt-10" data-id="element-2975">
        <div className="flex items-center justify-between gap-4" data-id="element-2976">
          <div data-id="element-2977">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0F9E8E]" data-id="element-2978">
              Your sets
            </p>
            <h3 className="mt-2 text-xl font-bold text-[#0F172A]" data-id="element-2979">
              Personal flashcards
            </h3>
          </div>
          {!customSetsLoading && <span className="text-xs font-medium text-[#64748B]" data-id="element-2980">
              {customSets.length} {customSets.length === 1 ? 'set' : 'sets'}
            </span>}
        </div>

        {customSetsLoading ? <div className="mt-5 flex items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-6 text-sm text-[#64748B]" data-id="element-2981">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" data-id="element-2982" />
            Loading your saved sets…
          </div> : customSetsError ? <p role="alert" className="mt-5 rounded-2xl border border-[#FBD5D0] bg-[#FEF2F2] p-5 text-sm leading-relaxed text-[#B91C1C]" data-id="element-2983">
            {customSetsError}
          </p> : customSets.length ? <div className="mt-5 grid gap-5 md:grid-cols-3" data-id="element-2984">
            {customSets.map(set => <FlashcardSetCard key={set.id} set={set} onSelect={onSelect} data-id="element-2985" />)}
          </div> : <div className="mt-5 rounded-2xl border border-dashed border-[#A7DCD5] bg-[#F8FCFB] p-6 text-sm leading-relaxed text-[#52627E]" data-id="element-2986">
            You have not created a set yet. Add cards from your notes and they
            will appear here whenever you return.
          </div>}
      </div>

      <div className="mt-10" data-id="element-2987">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]" data-id="element-2988">
          Built-in decks
        </p>
        <h3 className="mt-2 text-xl font-bold text-[#0F172A]" data-id="element-2989">
          RD Ready study sets
        </h3>
        <div className="mt-5 grid gap-5 md:grid-cols-3" data-id="element-2990">
          {builtInSets.map(set => <FlashcardSetCard key={set.id} set={set} onSelect={onSelect} data-id="element-2991" />)}
        </div>
      </div>
    </section>;
}
function FlashcardSetCard({
  set,
  onSelect
}: {
  set: FlashcardSet;
  onSelect: (set: FlashcardSet) => void;
}) {
  return <button type="button" onClick={() => onSelect(set)} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-left shadow-[0_2px_16px_rgba(15,23,42,.04)] transition-colors hover:border-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-2992">
      <BookOpenCheckIcon className={`h-7 w-7 ${set.isCustom ? 'text-[#0F9E8E]' : 'text-[#2563EB]'}`} data-id="element-2993" />
      <div className="mt-5 flex items-center justify-between gap-3" data-id="element-2994">
        <h4 className="text-xl font-bold text-[#0F172A]" data-id="element-2995">{set.title}</h4>
        {set.isCustom && <span className="shrink-0 rounded-full bg-[#F0FBF9] px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#0F766E]" data-id="element-2996">
            Yours
          </span>}
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2997">
        {set.description}
      </p>
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]" data-id="element-2998">
        {set.cardCount} {set.cardCount === 1 ? 'card' : 'cards'}{' '}
        <ArrowRightIcon className="h-4 w-4" data-id="element-2999" />
      </span>
    </button>;
}
function CreateFlashcardSet({
  onCancel,
  onSave
}: {
  onCancel: () => void;
  onSave: (set: CustomFlashcardSet) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cards, setCards] = useState([{
    topic: '',
    front: '',
    back: ''
  }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const updateCard = (index: number, field: 'topic' | 'front' | 'back', value: string) => {
    setCards(current => current.map((card, cardIndex) => cardIndex === index ? {
      ...card,
      [field]: value
    } : card));
  };
  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    const cleanTitle = title.trim();
    const cleanDescription = description.trim();
    const cleanCards = cards.map(card => ({
      topic: card.topic.trim(),
      front: card.front.trim(),
      back: card.back.trim()
    })).filter(card => card.front || card.back);
    if (!cleanTitle) {
      setError('Give your flashcard set a name before saving.');
      return;
    }
    if (!cleanDescription) {
      setError('Add a short description to help you recognize this set.');
      return;
    }
    if (!cleanCards.length) {
      setError('Add at least one prompt and answer card before saving.');
      return;
    }
    if (cleanCards.some(card => !card.front || !card.back)) {
      setError('Each card needs both a prompt and an answer.');
      return;
    }
    const now = new Date().toISOString();
    setSaving(true);
    try {
      await onSave({
        id: `custom-${now.replace(/[^0-9]/g, '')}-${Math.random().toString(36).slice(2, 7)}`,
        title: cleanTitle,
        description: cleanDescription,
        cards: cleanCards.map(card => {
          const cleanCard: {
            front: string;
            back: string;
            topic?: string;
          } = {
            front: card.front,
            back: card.back
          };
          if (card.topic) cleanCard.topic = card.topic;
          return cleanCard;
        }),
        createdAt: now,
        updatedAt: now
      });
      setSuccess('Your set has been saved to your account.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not save your set. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return <section className="mx-auto max-w-3xl" data-id="element-3000">
      <button type="button" onClick={onCancel} disabled={saving} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B] transition-colors hover:text-[#2563EB] disabled:opacity-60" data-id="element-3001">
        <ChevronLeftIcon className="h-4 w-4" data-id="element-3002" /> All sets
      </button>
      <div className="mt-5 rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-[0_4px_28px_rgba(15,23,42,.06)] md:p-9" data-id="element-3003">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-3004">
          Your private library
        </p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-3005">
          Create your own set
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-3006">
          Build a focused deck from your own study notes. It will save to your
          account and be available on any device where you sign in.
        </p>

        <form onSubmit={save} className="mt-8" data-id="element-3007">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-3008">
            <label className="text-sm font-semibold text-[#334155] sm:col-span-2" data-id="element-3009">
              Set name
              <input required maxLength={80} value={title} onChange={event => setTitle(event.target.value)} placeholder="e.g. Renal nutrition review" className={fieldClass} data-id="element-3010" />
            </label>
            <label className="text-sm font-semibold text-[#334155] sm:col-span-2" data-id="element-3011">
              Description
              <textarea required rows={2} maxLength={180} value={description} onChange={event => setDescription(event.target.value)} placeholder="A quick reminder of what this deck covers" className={`${fieldClass} resize-y`} data-id="element-3012" />
            </label>
          </div>

          <div className="mt-8 border-t border-[#E2E8F0] pt-7" data-id="element-3013">
            <div className="flex items-center justify-between gap-4" data-id="element-3014">
              <div data-id="element-3015">
                <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-3016">Cards</h3>
                <p className="mt-1 text-sm text-[#64748B]" data-id="element-3017">
                  Add a prompt and its answer. Topics are optional.
                </p>
              </div>
              <span className="rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-bold text-[#2563EB]" data-id="element-3018">
                {cards.length} {cards.length === 1 ? 'card' : 'cards'}
              </span>
            </div>

            <div className="mt-5 space-y-5" data-id="element-3019">
              {cards.map((card, index) => <fieldset key={index} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5" data-id="element-3020">
                  <legend className="sr-only" data-id="element-3021">Card {index + 1}</legend>
                  <div className="flex items-center justify-between gap-4" data-id="element-3022">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]" data-id="element-3023">
                      Card {index + 1}
                    </p>
                    {cards.length > 1 && <button type="button" onClick={() => setCards(current => current.filter((_, cardIndex) => cardIndex !== index))} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#B91C1C] transition-colors hover:text-[#991B1B]" data-id="element-3024">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-3025" /> Remove
                      </button>}
                  </div>
                  <label className="mt-4 block text-sm font-semibold text-[#334155]" data-id="element-3026">
                    Topic{' '}
                    <span className="font-normal text-[#94A3B8]" data-id="element-3027">
                      (optional)
                    </span>
                    <input maxLength={60} value={card.topic} onChange={event => updateCard(index, 'topic', event.target.value)} placeholder="e.g. Electrolytes" className={fieldClass} data-id="element-3028" />
                  </label>
                  <label className="mt-4 block text-sm font-semibold text-[#334155]" data-id="element-3029">
                    Prompt
                    <textarea required rows={3} value={card.front} onChange={event => updateCard(index, 'front', event.target.value)} placeholder="Write the question or cue" className={`${fieldClass} resize-y`} data-id="element-3030" />
                  </label>
                  <label className="mt-4 block text-sm font-semibold text-[#334155]" data-id="element-3031">
                    Answer
                    <textarea required rows={3} value={card.back} onChange={event => updateCard(index, 'back', event.target.value)} placeholder="Write the answer you want to remember" className={`${fieldClass} resize-y`} data-id="element-3032" />
                  </label>
                </fieldset>)}
            </div>

            <button type="button" onClick={() => setCards(current => [...current, {
            topic: '',
            front: '',
            back: ''
          }])} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#93C5FD] bg-white px-4 py-2.5 text-sm font-semibold text-[#2563EB] transition-colors hover:border-[#2563EB] disabled:opacity-60" data-id="element-3033">
              <PlusIcon className="h-4 w-4" data-id="element-3034" /> Add another card
            </button>
          </div>

          <div aria-live="polite" className="mt-6" data-id="element-3035">
            {error && <p role="alert" className="rounded-xl border border-[#FBD5D0] bg-[#FEF2F2] p-4 text-sm font-medium text-[#B91C1C]" data-id="element-3036">
                {error}
              </p>}
            {success && <p className="rounded-xl border border-[#C7EFE8] bg-[#F0FBF9] p-4 text-sm font-medium text-[#0F766E]" data-id="element-3037">
                {success}
              </p>}
          </div>

          <div className="mt-7 flex flex-wrap gap-3" data-id="element-3038">
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" data-id="element-3039">
              {saving ? <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-3040" />
                  Saving set…
                </> : <>
                  Save to my account <ArrowRightIcon className="h-4 w-4" data-id="element-3041" />
                </>}
            </button>
            <button type="button" onClick={onCancel} disabled={saving} className="rounded-full border border-[#CBD5E1] px-6 py-3 text-sm font-semibold text-[#475569] transition-colors hover:border-[#2563EB] hover:text-[#2563EB] disabled:opacity-60" data-id="element-3042">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>;
}
function Flashcards({
  set,
  onBack,
  onFinish
}: {
  set: FlashcardSet;
  onBack: () => void;
  onFinish: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const card = set.cards[index];
  const change = (direction: number) => {
    setIndex(value => (value + direction + set.cards.length) % set.cards.length);
    setShowBack(false);
  };
  return <section className="mx-auto max-w-3xl" data-id="element-3043">
      <div className="mb-5 flex items-center justify-between gap-4 text-xs font-bold uppercase tracking-[0.14em] text-[#94A3B8]" data-id="element-3044">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 transition-colors hover:text-[#2563EB]" data-id="element-3045">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-3046" /> All sets
        </button>
        <span data-id="element-3047">
          {index + 1} / {set.cards.length}
        </span>
      </div>
      <p className="mb-4 text-sm font-semibold text-[#475569]" data-id="element-3048">{set.title}</p>
      <button type="button" onClick={() => setShowBack(value => !value)} aria-pressed={showBack} aria-label={showBack ? 'Tap to review the prompt' : 'Tap to reveal the answer'} className="block w-full rounded-3xl [perspective:1600px]" data-id="element-3049">
        {/* Remount per card (key by index) so a new card appears front-facing
            instantly instead of animating a reverse flip on navigation. */}
        <motion.div key={index} className="relative h-[340px] w-full [transform-style:preserve-3d]" initial={false} animate={{
        rotateY: showBack ? 180 : 0
      }} transition={{
        duration: 0.5,
        ease: [0.22, 0.61, 0.36, 1]
      }} data-id="element-3050">
          {/* FRONT */}
          <FlashcardFace topic={card.topic} body={card.front} hint="Tap to reveal the answer" data-id="element-3051" />
          {/* BACK — pre-rotated so it faces the viewer once the card turns */}
          <FlashcardFace topic={card.topic} body={card.back} hint="Tap to review the prompt" back data-id="element-3052" />
        </motion.div>
      </button>
      <div className="mt-7 flex items-center justify-between" data-id="element-3053">
        <button type="button" onClick={() => change(-1)} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#475569] transition-colors hover:text-[#2563EB]" data-id="element-3054">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-3055" /> Previous
        </button>
        <button type="button" onClick={onFinish} className="text-xs font-bold uppercase tracking-[0.12em] text-[#94A3B8] transition-colors hover:text-[#0F172A]" data-id="element-3056">
          Finish review
        </button>
        <button type="button" onClick={() => change(1)} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#475569] transition-colors hover:text-[#2563EB]" data-id="element-3057">
          Next <ChevronRightIcon className="h-4 w-4" data-id="element-3058" />
        </button>
      </div>
    </section>;
}
function FlashcardFace({
  topic,
  body,
  hint,
  back = false
}: {
  topic: string;
  body: string;
  hint: string;
  back?: boolean;
}) {
  return <div className={`absolute inset-0 flex flex-col justify-between rounded-3xl border border-[#E2E8F0] p-7 text-left shadow-[0_4px_28px_rgba(15,23,42,.06)] [backface-visibility:hidden] md:p-10 ${back ? 'bg-[#F7F9FC] [transform:rotateY(180deg)]' : 'bg-white'}`} data-id="element-3059">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{
      color: TEAL
    }} data-id="element-3060">
        {topic}
      </span>
      <div className="max-w-2xl" data-id="element-3061">
        <p className="text-2xl font-bold leading-snug text-[#0F172A] md:text-3xl" data-id="element-3062">
          {body}
        </p>
        <p className="mt-8 text-sm text-[#94A3B8]" data-id="element-3063">{hint}</p>
      </div>
      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em]" style={{
      color: BLUE
    }} data-id="element-3064">
        <RotateCcwIcon className="h-4 w-4" data-id="element-3065" /> Active recall
      </span>
    </div>;
}